// -------------------------------------------------------------------------------------------------
// NortonLifeLock copyright header start
// ----------------------
//
// PROPRIETARY / CONFIDENTIAL.
// Use of this product is subject to license terms.
// Copyright (c) 2022 NortonLifeLock
// All rights reserved.
//
// -------------------------------------------------------------------------------------------------
// NortonLifeLock copyright header stop
// ----------------------
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  AntiTrackYellowIcon,
  NonEnglishSetupAnimation,
  PageContent,
  SetupCompleteAnimation,
} from '../../components';
import styled from 'styled-components';
import puzzle from '../../assets/images/puzzle.webp';
import emptypuzzle from '../../assets/images/puzzle-empty.webp';
import { isEnglishLang, isIphone } from '../../utils';
import {
  NonEnglishSetupAnimationIpad,
  SetupCompleteAnimationIpad,
} from '../../components/Base64Animation/Base64AnimationIpad';

import { responsive } from '../../components';

const PageContainer = styled(PageContent)`
  padding-top: 2.8125rem;

  ${responsive.tablet`
    align-items: center;
  `}
`;

const Title = styled.h1``;

const Description = styled.p`
  margin: 1.5625rem 0 3.5625rem;

  svg {
    width: 1.5rem;
    height: 1.5rem;
    transform: translateY(0.375rem);
    margin: 0 0.3125rem;
  }
`;

const Icon = styled.img.attrs({ alt: 'puzzle-icon' })`
  margin: 0 0.3125rem;
  transform: translateY(0.1875rem);
`;

const getAnimation = () => {
  if (isIphone()) {
    return isEnglishLang() ? SetupCompleteAnimation : NonEnglishSetupAnimation;
  } else {
    return isEnglishLang() ? SetupCompleteAnimationIpad : NonEnglishSetupAnimationIpad;
  }
};

const Clip = styled(getAnimation()).attrs(() => ({
  'data-is-blurred': !isEnglishLang(),
  'data-testid': 'clip-animation',
}))`
  width: 15.625rem;
  height: 19.75rem;
  align-self: center;
  animation: clip-animation 4s 1s cubic-bezier(0.81, -0.31, 0.06, 1) infinite;
  background-repeat: no-repeat;
`;

const ClipIpad = styled(getAnimation()).attrs(() => ({
  'data-is-blurred': !isEnglishLang(),
  'data-testid': 'clip-animation-ipad',
}))`
  width: 31.25rem;
  height: 45.125rem;
  align-self: center;
  animation: clip-animation 4s 1s cubic-bezier(0.81, -0.31, 0.06, 1) infinite;
  background-repeat: no-repeat;
  background-size: contain;
`;

export const CompletePage = () => {
  const { t } = useTranslation();

  return (
    <PageContainer>
      <Title>{t('Your setup is complete!')}</Title>
      <Description>
        {isIphone() ? (
          <Trans t={t} i18nKey='OpenExtension'>
            Open AntiTrack extension by tapping <Icon src={puzzle} /> to the left of the address
            bar.
          </Trans>
        ) : (
          <Trans t={t} i18nKey='OpenExtensionIpad'>
            Open AntiTrack extension by tapping <AntiTrackYellowIcon /> or
            <Icon src={emptypuzzle} /> on the Safari address bar.
          </Trans>
        )}
      </Description>
      {isIphone() ? <Clip /> : <ClipIpad />}
    </PageContainer>
  );
};
